import React, { createContext, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { WithChildren } from '../models/children-props';
import { PostEditorContextEnum, PostEditorState } from '../models/post-editor';
import { PostEditorPublicAction } from '../modules/PostEditor/states/PostEditorActions';
import { RightDrawerTitles, RightDrawerTypeEnum } from '../constants/right-drawer';
import { PostSource, useGetUsageFieldsLazyQuery } from '../models/graphql';
import { IInvitation, ISocialOAuth } from '../models/auth';
import { makeVar, ReactiveVar } from '@apollo/client';
import usePolling from 'hooks/usePolling';
import { LimitReachModalState } from 'modules/PlanLimit/LimitReachedModal/LimitReachedModal.view';

export type AppStateType = {
  loadingOverlayVisible: boolean;
  selectedWorkspaceId: string;
};

export type PostEditorContextTypeNew = {
  type: PostEditorContextEnum.new;
  initialState: PostEditorState;
  overrideWorkspaceSettings?: true;
  payload?: PostEditorPublicAction | PostEditorPublicAction[];
  onClose?: OnClosePostEditorAction;
};

export type PostEditorContextTypeRescheduleOrDuplicate = {
  type: PostEditorContextEnum.reschedule_new | PostEditorContextEnum.duplicate;
  postId: string;
  sourceType: PostSource;
  overrideWorkspaceSettings?: true;
  payload?: PostEditorPublicAction | PostEditorPublicAction[];
  onClose?: OnClosePostEditorAction;
};

export type PostEditorTypeUndefined = {
  type: PostEditorContextEnum.undefined;
};

export type PostEditorContextTypeEdit = {
  type: PostEditorContextEnum.edit | PostEditorContextEnum.reschedule_edit | PostEditorContextEnum.restore_trashed;
  postId: string;
  payload?: PostEditorPublicAction | PostEditorPublicAction[];
  onClose?: OnClosePostEditorAction;
};

export type PostEditorContextTypeImport = {
  type: PostEditorContextEnum.import;
  initialState: PostEditorState;
  payload?: PostEditorPublicAction | PostEditorPublicAction[];
  onClose?: OnClosePostEditorAction;
  overrideWorkspaceSettings?: true;
};

export const isPostEditorContextTypeNewGuard = (value: PostEditorContextType): value is PostEditorContextTypeNew =>
  PostEditorContextEnum.new === value.type;

export const isPostEditorContextTypeRescheduleOrDuplicateGuard = (
  value: PostEditorContextType
): value is PostEditorContextTypeRescheduleOrDuplicate =>
  [PostEditorContextEnum.duplicate, PostEditorContextEnum.reschedule_new].includes(value.type);

export const isPostEditorContextTypeEditGuard = (value: PostEditorContextType): value is PostEditorContextTypeEdit =>
  [PostEditorContextEnum.edit, PostEditorContextEnum.reschedule_edit, PostEditorContextEnum.restore_trashed].includes(
    value.type
  );

export type PostEditorContextType =
  | PostEditorTypeUndefined
  | PostEditorContextTypeNew
  | PostEditorContextTypeRescheduleOrDuplicate
  | PostEditorContextTypeEdit
  | PostEditorContextTypeImport;

export type OnClosePostEditorAction = (success: boolean) => void;
export type RightDrawerToggleHandler = (type: RightDrawerTypeEnum | null) => void;

export type RightDrawerContext = {
  title: string;
  type: RightDrawerTypeEnum | null;
  open: boolean;
};

export enum AppEnvironment {
  DEV = 'DEV',
  PROD = 'PROD'
}

export type AppConfigType = {
  environment: AppEnvironment;
  canvaApiKey: string | null;
  hotjarApiId: string | null;
};

type UsagePollingState = {
  shouldPoll: boolean;
  amountPoll: number;
  interval: number;
};

type AppValueType = {
  appConfig: AppConfigType;
  loadingOverlayVisible: boolean;
  postEditorContext: PostEditorContextType;
  selectedWorkspaceId: AppStateType['selectedWorkspaceId'];
  isLoggedIn: boolean;
  setPostEditorContext: Dispatch<SetStateAction<PostEditorContextType>>;
  showLoadingOverlay: Dispatch<SetStateAction<boolean>>;
  rightDrawerState: RightDrawerContext;
  toggleRightDrawer: RightDrawerToggleHandler;
  limitReachModalState: LimitReachModalState;
  setLimitReachedModalState: Dispatch<SetStateAction<LimitReachModalState>>;
  isSuspendedModalOpen: boolean;
  setSuspendedModalOpen: Dispatch<SetStateAction<boolean>>;
  setPollUsage: Dispatch<SetStateAction<UsagePollingState>>;
};

type AppProviderPropsType = WithChildren & {
  selectedWorkspaceId: AppStateType['selectedWorkspaceId'];
  isLoggedIn: boolean;
};

const getAppConfigFromEnv = (): AppConfigType => {
  let canvaApiKey: string | null = process.env.REACT_APP_APPCONFIG_CANVA_API_KEY as string;
  if (!canvaApiKey || canvaApiKey.toLowerCase() === 'null') {
    canvaApiKey = null;
  }

  let hotjarApiId: string | null = process.env.REACT_APP_APPCONFIG_HOTJAR_API_ID as string;
  if (!hotjarApiId || hotjarApiId.toLowerCase() === 'null') {
    hotjarApiId = null;
  }

  let environment = process.env.REACT_APP_APPCONFIG_ENVIRONMENT as AppEnvironment;
  if (!Object.values(AppEnvironment).includes(environment)) {
    console.error(
      `Environment "${environment}" not supported, defaulting to "${AppEnvironment.PROD}" - please ensure proper environment variable is set`
    );
    environment = AppEnvironment.PROD;
  }

  return {
    environment,
    canvaApiKey,
    hotjarApiId
  };
};

export const AppContextInitialState: AppValueType = {
  appConfig: getAppConfigFromEnv(),
  loadingOverlayVisible: false,
  postEditorContext: { type: PostEditorContextEnum.undefined },
  selectedWorkspaceId: '',
  isLoggedIn: false,
  setPostEditorContext: () => {
    /**/
  },
  showLoadingOverlay: () => {
    /**/
  },
  rightDrawerState: {
    title: '',
    type: null,
    open: false
  },
  toggleRightDrawer: () => {
    /**/
  },
  limitReachModalState: { isOpen: false },
  setLimitReachedModalState: () => {
    //
  },
  isSuspendedModalOpen: false,
  setSuspendedModalOpen: () => {
    //
  },
  setPollUsage: () => {
    //
  }
};

export const AppContext = createContext<AppValueType>(AppContextInitialState);

export const AppProvider = ({ selectedWorkspaceId, isLoggedIn, children }: AppProviderPropsType): JSX.Element => {
  const [loadingOverlayVisible, showLoadingOverlay] = useState<boolean>(false);
  const [pollUsage, setPollUsage] = useState({
    shouldPoll: false,
    amountPoll: 0,
    interval: 0
  });
  const [postEditorContext, setPostEditorContext] = useState<PostEditorContextType>({
    type: PostEditorContextEnum.undefined
  });
  const [getUsageFields] = useGetUsageFieldsLazyQuery();
  const [limitReachModalState, setLimitReachedModalState] = useState<LimitReachModalState>({
    isOpen: false
  });
  const [isSuspendedModalOpen, setSuspendedModalOpen] = useState(false);
  const [rightDrawerState, setRightDrawerState] = useState<RightDrawerContext>({
    title: '',
    type: null,
    open: false
  });
  const appConfig = AppContextInitialState.appConfig;
  const currentPolling = useRef(false);
  const { startPolling, stopPolling } = usePolling();

  useEffect(() => {
    if (pollUsage.shouldPoll && !currentPolling.current) {
      currentPolling.current = true;
      startPolling(() => {
        getUsageFields({ fetchPolicy: 'network-only' });
        setPollUsage(prevState => ({ ...prevState, amountPoll: prevState.amountPoll - 1 }));
      }, pollUsage.interval);
    } else if (pollUsage.amountPoll === 0) {
      stopPolling();
      currentPolling.current = false;
    }
  }, [pollUsage.shouldPoll, pollUsage.interval, pollUsage.amountPoll, getUsageFields, startPolling, stopPolling]);

  const toggleRightDrawer: RightDrawerToggleHandler = useCallback(
    type => {
      setRightDrawerState(oldRightDrawer => {
        let willBeOpen;
        let rightDrawerTitle = '';

        if (type === null) {
          willBeOpen = false;
        } else {
          willBeOpen = oldRightDrawer.open && type === oldRightDrawer.type ? false : !oldRightDrawer.open;
          willBeOpen = oldRightDrawer.type && !oldRightDrawer.title ? false : willBeOpen;
          willBeOpen = oldRightDrawer.title !== RightDrawerTitles[type] ? true : willBeOpen;
          rightDrawerTitle = RightDrawerTitles[type];
        }
        return {
          title: rightDrawerTitle,
          type: type,
          open: willBeOpen
        };
      });
    },
    [setRightDrawerState]
  );

  return (
    <AppContext.Provider
      value={{
        appConfig,
        loadingOverlayVisible,
        postEditorContext,
        selectedWorkspaceId,
        isLoggedIn,
        setPostEditorContext,
        showLoadingOverlay,
        rightDrawerState,
        toggleRightDrawer,
        limitReachModalState,
        setLimitReachedModalState,
        setPollUsage,
        isSuspendedModalOpen,
        setSuspendedModalOpen
      }}>
      {children}
    </AppContext.Provider>
  );
};
const getOAuthObject = (): ISocialOAuth | null => {
  const socialOAuth = sessionStorage.getItem('socialOAuth');
  sessionStorage.removeItem('socialOAuth');
  try {
    return socialOAuth ? JSON.parse(socialOAuth) : null;
  } catch (e) {
    return null;
  }
};

const getInvitationObject = (): IInvitation | null => {
  const invitation = sessionStorage.getItem('invitation');
  sessionStorage.removeItem('invitation');
  try {
    return invitation ? JSON.parse(invitation) : null;
  } catch (e) {
    return null;
  }
};

export const socialOAuthVar: ReactiveVar<ISocialOAuth | null> = makeVar<ISocialOAuth | null>(getOAuthObject());

export const invitationDetailsVar: ReactiveVar<IInvitation | null> = makeVar<IInvitation | null>(getInvitationObject());

let changeSelectedWorkspaceCalled = false;
export const changeSelectedWorkspaceId = (workspaceId: string, path?: string): void => {
  if (changeSelectedWorkspaceCalled) {
    return;
  }
  changeSelectedWorkspaceCalled = true;

  // Import just-in-time to avoid circular dependency issues, as rootReactHelpers imports a bunch of core app files.
  import('helpers/rootReactHelpers').then(({ _resetApp }) => {
    if (workspaceId) {
      localStorage.setItem('selectedWorkspaceId', workspaceId);
    } else {
      localStorage.removeItem('selectedWorkspaceId');
    }
    if (path) {
      window.history.pushState({}, '', path);
    }
    socialOAuthVar(getOAuthObject());
    invitationDetailsVar(getInvitationObject());
    _resetApp();
    changeSelectedWorkspaceCalled = false;
  });
};

const urlSearchParams = new URLSearchParams(window.location.search);
export const isFromChromeExtension =
  urlSearchParams.get('src') === 'CHROME_EXTENSION' || sessionStorage.getItem('fromChromeExtension') !== null;
if (isFromChromeExtension) {
  sessionStorage.removeItem('fromChromeExtension');
}
